import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface CampaignOutcomes {
  BOOKED: number;
  DECLINED_OK_TO_RECALL: number;
  DECLINED_DO_NOT_CALL: number;
  NO_ANSWER: number;
  BUSY: number;
  FAILED: number;
  TECHNICAL_ERROR: number;
}

export interface ActiveCampaignResponse {
  id: string;
  status: "RUNNING" | "CANCELLING" | "CANCELLED" | "COMPLETED";
  mode: "LIVE" | "SIMULATION";
  totalClients: number;
  pending: number;
  dispatched: number;
  done: number;
  skipped: number;
  startedAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  pausedAt: string | null;
  pauseReason: string | null;
  outcomes: CampaignOutcomes;
}

export interface CampaignPreviewResponse {
  eligibleCount: number;
  ineligibleCount: number;
  breakdown: {
    doNotCall: number;
    dncRegister: number;
    staleWash: number;
    alreadyBooked: number;
    alreadyScheduled: number;
    inActiveBatch: number;
  };
  estimatedCostAUD: number;
  dryRunActive: boolean;
  activeBlocker: string | null;
}

function isActiveBatch(status: string) {
  return status === "RUNNING" || status === "CANCELLING";
}

export function useActiveCampaign() {
  return useQuery<ActiveCampaignResponse | null>({
    queryKey: ["campaign-active"],
    queryFn: () => api<ActiveCampaignResponse | null>("/api/campaigns/active"),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return isActiveBatch(data.status) ? 5_000 : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 4_000,
  });
}

export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      api<{ ok: boolean }>(`/api/campaigns/${batchId}/cancel`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-active"] }),
  });
}

export function usePauseCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      api<{ ok: boolean }>(`/api/campaigns/${batchId}/pause`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-active"] }),
  });
}

export function useResumeCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      api<{ ok: boolean }>(`/api/campaigns/${batchId}/resume`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-active"] }),
  });
}

export function useDismissCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      api<{ ok: boolean }>(`/api/campaigns/${batchId}/dismiss`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-active"] }),
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { clientIds?: string[]; mode?: "LIVE" | "SIMULATION" }) =>
      api<{ batchId: string; mode: string; totalClients: number; skipped: number }>(
        "/api/campaigns",
        { method: "POST", body: JSON.stringify(params) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-active"] }),
  });
}

export function previewCampaign(
  clientIds?: string[],
  mode: "LIVE" | "SIMULATION" = "LIVE",
): Promise<CampaignPreviewResponse> {
  return api<CampaignPreviewResponse>("/api/campaigns/preview", {
    method: "POST",
    body: JSON.stringify({ clientIds, mode }),
  });
}
